<template>
  <b-modal
    :id="modalId"
    ref="modal-season-pass"
    :hide-footer="true"
    :hide-header="true"
    :style="styleVars"
    modal-class="modal-season-pass"
    @shown="onOpen"
    @hidden="onClose"
  >
    <template slot="default">
      <Spinner
        v-if="isOpen && loading"
        size="medium"
        context="Loading Season Pass..."
        classes="dead-center"
      ></Spinner>
      <div
        v-if="isOpen && !loading"
        class="season-pass-canvas"
        :style="canvasStyle"
      >
        <a href="#" class="close-button" @click="hide"></a>

        <div v-if="endDate" class="timer d-inline-flex align-items-center">
          <div class="icon-hourglass"></div>
          <countdown-digital
            :fixed-width="true"
            :time="endDate"
            :days-to-hours="true"
            :with-time-diff="true"
          ></countdown-digital>
        </div>

        <div class="help-button pointer" @click="openHelp"></div>

        <img v-if="sidebarUrl" class="bg sidebar" :src="sidebarUrl" alt="" />

        <div
          class="sidebar-state-icon"
          :class="isPremium ? 'check-icon' : 'lock-icon'"
        ></div>

        <div v-if="premiumTierLabel" class="sidebar-tier-label premium">
          {{ premiumTierLabel }}
        </div>
        <div v-if="freeTierLabel" class="sidebar-tier-label free">
          {{ freeTierLabel }}
        </div>

        <SeasonPassTaskPanel
          v-if="pass"
          :pass="pass"
          :user-data="userData"
          :is-premium="isPremium"
          :tickets-enabled="ticketsEnabled"
          :ticket-count="ticketCount"
          :current-task="currentTask"
          :next-locked-level="nextLockedLevel"
          :tier-name="premiumTierLabel"
          @buy-premium="onBuyPremiumClick"
        />

        <SeasonPassRewardsStrip
          v-if="pass && rewards.length"
          :pass="pass"
          :rewards="rewards"
          :current-level-index="currentLevelIndex"
          :user-progress="userProgress"
          :is-premium="isPremium"
          :tickets-enabled="ticketsEnabled"
          :unlock-in-flight="unlockInFlight"
          :lock-panel-level="nextLockedLevel"
          @unlock-level="onUnlockLevel"
          @buy-tier-offer="onBuyTierOffer"
        />

        <ModalSeasonPassHelp
          v-if="pass"
          :help-config="helpConfig"
          :pass-id="pass.id"
          :pass-name="passName"
          :levels-count="levelsCount"
          :premium-label="premiumTierLabel"
          :premium-cta="premiumCtaLabel"
          :is-premium="isPremium"
          :premium-prices="premiumPrices"
          @buy-premium="onBuyPremiumClick"
        />

        <AnimationSeasonpassUnlockLevel
          v-if="showLevelUnlockAnimation"
          :level="lastUnlockedLevel"
          @seasonpass-ani-completed="showLevelUnlockAnimation = false"
        />

        <AnimationSeasonpassKingspass
          v-if="showPremiumActivatedAnimation"
          :tier-label="premiumTierLabel"
          :badge-url="passBadgeUrl"
          @seasonpass-kingspass-ani-completed="
            showPremiumActivatedAnimation = false
          "
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import SeasonPassTaskPanel from "./season-pass-task-panel.vue";
import SeasonPassRewardsStrip from "./season-pass-rewards-strip.vue";
import ModalSeasonPassHelp from "./modal-season-pass-help.vue";
import CountdownDigital from "@/components/base/countdown-digital";
import AnimationSeasonpassUnlockLevel from "@/components/animation/animation-spine-seasonpass-unlock-level";
import AnimationSeasonpassKingspass from "@/components/animation/animation-spine-seasonpass-kingspass";
import Spinner from "@/components/animation/animation-spinner.vue";
import offerModal from "@/mixins/offerModal.js";
import AnimationParsingFunctions from "@/components/animation/animation-parsing-functions";

export default {
  name: "ModalSeasonPass",
  components: {
    SeasonPassTaskPanel,
    SeasonPassRewardsStrip,
    ModalSeasonPassHelp,
    CountdownDigital,
    AnimationSeasonpassUnlockLevel,
    AnimationSeasonpassKingspass,
    Spinner,
  },
  mixins: [offerModal, AnimationParsingFunctions],
  props: {
    modalId: {
      type: String,
      default: "modal-season-pass",
    },
  },

  data() {
    return {
      unlockInFlight: false,
      isOpen: false,
      loading: true,
      showLevelUnlockAnimation: false,
      showPremiumActivatedAnimation: false,
      lastUnlockedLevel: 0,
    };
  },

  computed: {
    pass() {
      return this.$store.getters["seasonPass/getCurrentSeasonPass"];
    },
    passId() {
      return this.pass && this.pass.id;
    },
    displayConfig() {
      if (!this.pass) return {};
      const raw = this.pass.display_config || this.pass.displayConfig;
      if (!raw) return {};
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (e) {
          return {};
        }
      }
      return raw;
    },
    backgroundUrl() {
      return (
        this.fullBackgroundUrl ||
        this.getMediaUrl(this.pass, "background") ||
        this.getConfigUrl(this.displayConfig, "background")
      );
    },
    canvasStyle() {
      return this.backgroundUrl
        ? { backgroundImage: `url("${this.backgroundUrl}")` }
        : {};
    },
    fullBackgroundUrl() {
      return (
        this.getMediaUrl(this.pass, "bg_full") ||
        this.getMediaUrl(this.pass, "bg-full") ||
        this.getMediaUrl(this.pass, "bgFull") ||
        this.getConfigUrl(this.displayConfig, "bg_full") ||
        this.getConfigUrl(this.displayConfig, "bg-full") ||
        this.getConfigUrl(this.displayConfig, "bgFull") ||
        this.getConfigUrl(this.displayConfig, "background_full") ||
        this.getConfigUrl(this.displayConfig, "backgroundFull")
      );
    },
    sidebarUrl() {
      return (
        this.getMediaUrl(this.pass, "sidebar") ||
        this.getConfigUrl(this.displayConfig, "sidebar")
      );
    },
    passBadgeUrl() {
      return this.pass ? this.extractSeasonPassBadgeUrl(this.pass) : "";
    },
    locale() {
      return this.$store.state.locale || "de";
    },
    freeTierLabel() {
      return this.tierLabel(1);
    },
    premiumTierLabel() {
      return this.tierLabel(2);
    },
    helpConfig() {
      return {
        ...(this.displayConfig.help || {}),
        background:
          this.getMediaUrl(this.pass, "help") ||
          this.getConfigUrl(this.displayConfig.help || {}, "background"),
      };
    },
    passName() {
      return this.pass ? this.extractSeasonPassName(this.pass) : "";
    },
    levelsCount() {
      return (this.pass && this.pass.levels && this.pass.levels.length) || 0;
    },
    ticketsEnabled() {
      return this.displayConfig.ticketsEnabled === true;
    },
    styleVars() {
      const s = (this.displayConfig && this.displayConfig.style) || {};
      const vars = {};
      const tc = s["text-color"] || s.textColor;
      const hc = s["text-highlight-color"] || s.textHighlightColor;
      const mc =
        s["more-content-highlight-color"] || s.moreContentHighlightColor;
      if (tc) vars["--sp-text-color"] = tc;
      if (hc) vars["--sp-highlight-color"] = hc;
      if (mc) vars["--sp-more-highlight-color"] = mc;
      return vars;
    },
    rewards() {
      return this.passId
        ? this.$store.getters["seasonPass/rewards"](this.passId)
        : [];
    },
    userData() {
      return this.passId
        ? this.$store.getters["seasonPass/user"](this.passId)
        : null;
    },
    isPremium() {
      return this.passId
        ? this.$store.getters["seasonPass/isPremium"](this.passId)
        : false;
    },
    backendLevel() {
      const levels = (this.pass && this.pass.levels) || [];
      if (!levels.length || !this.userData) {
        return null;
      }

      const levelId = this.userData.level_id;
      const userLevel = this.userData.level;
      const embeddedLevelId = userLevel && userLevel.id;
      const levelNumber = userLevel && userLevel.level;

      return (
        levels.find((level) => level.id === levelId) ||
        levels.find((level) => level.id === embeddedLevelId) ||
        levels.find((level) => level.level === levelNumber) ||
        null
      );
    },
    backendLevelIndex() {
      const levels = (this.pass && this.pass.levels) || [];
      if (!levels.length || !this.backendLevel) return 0;

      const index = levels.findIndex(
        (level) => level.id === this.backendLevel.id
      );

      return index >= 0 ? index : 0;
    },
    backendLevelCompleted() {
      if (!this.userData) return false;
      return (
        this.userData.status === "DONE" || (this.userData.progress || 0) >= 100
      );
    },
    currentLevel() {
      const levels = (this.pass && this.pass.levels) || [];
      return levels[this.currentLevelIndex] || this.backendLevel;
    },
    currentLevelIndex() {
      const levels = (this.pass && this.pass.levels) || [];
      if (!levels.length) return 0;

      if (this.backendLevelCompleted) {
        const nextIndex = Math.min(
          this.backendLevelIndex + 1,
          levels.length - 1
        );
        if (this.isLevelTimeLocked(levels[nextIndex])) {
          return this.backendLevelIndex;
        }
        return nextIndex;
      }

      return this.backendLevelIndex;
    },
    userProgress() {
      if (this.backendLevelCompleted) {
        const levels = (this.pass && this.pass.levels) || [];
        const nextIndex = Math.min(
          this.backendLevelIndex + 1,
          levels.length - 1
        );
        if (this.isLevelTimeLocked(levels[nextIndex])) return 100;
        return 0;
      }
      return this.userData ? this.userData.progress || 0 : 0;
    },
    endDate() {
      return this.pass && this.pass.end_at
        ? new Date(this.pass.end_at).getTime()
        : 0;
    },
    currentTask() {
      return this.currentLevel;
    },
    ticketCount() {
      if (!this.ticketsEnabled) return 0;
      let counter = 0;
      for (let i = 0; i <= this.currentLevelIndex; i++) {
        counter += i;
      }
      if (this.userProgress >= 100) {
        counter += this.currentLevelIndex + 1;
      }
      if (this.isPremium) {
        counter *= 2;
      }
      return counter;
    },
    premiumTier() {
      const tiers = (this.pass && this.pass.tiers) || [];
      return tiers.find((t) => Number(t && t.priority) === 2) || null;
    },
    premiumPrices() {
      if (!this.premiumTier) return [];
      const prices = [
        {
          currency: "gold",
          amount: this.premiumTier.gold,
          icon: this.$whitelabel.currencies.primary.single,
        },
        {
          currency: "crowns",
          amount: this.premiumTier.crowns,
          icon: this.$whitelabel.currencies.secondary.single,
        },
      ];
      return prices.filter((price) => Number(price.amount) > 0);
    },
    premiumCtaLabel() {
      const cta = this.displayConfig.cta_text;
      if (cta && typeof cta === "object") {
        return cta[this.locale] || cta.fallback || cta.en || "";
      }
      if (typeof cta === "string") return cta;
      return "";
    },
    userGold() {
      const u = this.$store.state.user && this.$store.state.user.user;
      return (u && u.gold && u.gold.amount) || 0;
    },
    userCrowns() {
      const u = this.$store.state.user && this.$store.state.user.user;
      return (u && u.crowns && u.crowns.amount) || 0;
    },
    canAffordPremium() {
      if (!this.premiumTier) return false;
      const goldCost = Number(this.premiumTier.gold) || 0;
      const crownCost = Number(this.premiumTier.crowns) || 0;
      return this.userGold >= goldCost && this.userCrowns >= crownCost;
    },
    nextLockedLevel() {
      const levels = (this.pass && this.pass.levels) || [];
      const next = levels[this.currentLevelIndex + 1];
      if (!next) return null;
      if (!this.isLevelTimeLocked(next)) return null;
      if (this.userProgress < 100) return null;
      const hasCost =
        Number(next.gold) > 0 || Number(next.crowns) > 0 || !!next.pay_offer_id;
      if (!hasCost) return null;
      return next;
    },
  },
  watch: {
    userData: {
      handler(userData) {
        console.log("[seasonPass] modal user state", {
          userData,
          currentLevelIndex: this.currentLevelIndex,
          userProgress: this.userProgress,
          currentTask: this.currentTask,
        });
      },
      deep: true,
    },
  },

  methods: {
    isLevelTimeLocked(level) {
      const startAt = level && level.start_at;
      if (!startAt) return false;
      return new Date(startAt).getTime() > Date.now();
    },
    getMediaUrl(source, key) {
      const media = source && source[key];
      if (Array.isArray(media)) return (media[0] && media[0].url) || "";
      if (media && typeof media === "object") return media.url || "";
      if (Array.isArray(source && source.media)) {
        const match = source.media.find((item) =>
          [
            item.collection_name,
            item.collectionName,
            item.name,
            item.key,
            item.type,
          ].includes(key)
        );
        return (match && (match.url || match.original_url)) || "";
      }
      return typeof media === "string" ? media : "";
    },

    getConfigUrl(cfg, key) {
      if (!cfg) return "";
      return (
        cfg[key] ||
        cfg[`${key}Url`] ||
        cfg[`${key}_url`] ||
        cfg[`${key}Path`] ||
        cfg[`${key}_path`] ||
        cfg[`${key}PathFull`] ||
        cfg[`${key}_path_full`] ||
        ""
      );
    },

    tierLabel(priority) {
      const tiers = (this.pass && this.pass.tiers) || [];
      const tier = tiers.find((t) => Number(t.priority) === priority);
      if (!tier) return "";
      let cfg = tier.display_config || tier.displayConfig;
      if (typeof cfg === "string") {
        try {
          cfg = JSON.parse(cfg);
        } catch (e) {
          cfg = null;
        }
      }
      const text = cfg && cfg.text;
      if (text && typeof text === "object") {
        return (
          text[this.locale] || text.fallback || text.en || tier.title || ""
        );
      }
      return tier.title || "";
    },

    hide() {
      this.$bvModal.hide(this.modalId);
    },

    async onOpen() {
      this.isOpen = true;
      this.loading = true;

      try {
        // Always refetch — store is persisted, stale pass data (e.g. missing
        // tiers[].pay_offer) would otherwise survive across sessions.
        await this.$store.dispatch("seasonPass/fetchActivePasses");
        if (this.passId) {
          await this.$store.dispatch("seasonPass/loadActivePass", {
            passId: this.passId,
          });
        }
        await this.preloadAssets();
      } catch (e) {
        console.error("[seasonPass] open load failed", e);
      } finally {
        this.loading = false;
      }
    },

    onClose() {
      this.isOpen = false;
      this.loading = true;
    },

    preloadAssets() {
      const urls = this.collectAssetUrls();
      if (!urls.length) return Promise.resolve();
      return Promise.all(urls.map((u) => this.preloadImage(u)));
    },

    collectAssetUrls() {
      const urls = new Set();
      const add = (u) => {
        if (u && typeof u === "string") urls.add(u);
      };

      add(this.backgroundUrl);
      add(this.sidebarUrl);
      add(this.getMediaUrl(this.pass, "badge"));
      add(this.getMediaUrl(this.pass, "help"));
      add(this.getMediaUrl(this.pass, "tile_unlocked"));
      add(this.getMediaUrl(this.pass, "tile_locked"));

      const levels = (this.pass && this.pass.levels) || [];
      levels.forEach((level) => {
        if (Array.isArray(level.tile_unlocked)) {
          level.tile_unlocked.forEach((m) => add(m && m.url));
        }
        if (Array.isArray(level.tile_locked)) {
          level.tile_locked.forEach((m) => add(m && m.url));
        }
      });

      return Array.from(urls);
    },

    preloadImage(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = url;
      });
    },

    openHelp() {
      this.$bvModal.show("modal-season-pass-help");
    },

    onBuyPremiumClick() {
      if (!this.passId || !this.pass) return;
      if (this.isPremium) return;

      const premiumTier = this.premiumTier;
      if (!premiumTier) {
        console.warn("[seasonPass] no premium tier (priority=2) found");
        return;
      }

      if (this.canAffordPremium) {
        this.purchasePremiumTier(premiumTier);
        return;
      }

      if (premiumTier.pay_offer) {
        this.openDynamicPayOffer(premiumTier.pay_offer);
        return;
      }

      // Insufficient funds, no pay_offer: call purchase anyway. Backend rejects
      // and pushes the bailout offer via `.user.offer.unlocked` (handled in
      // wsChannels mixin), which surfaces the dynamic offer modal.
      this.purchasePremiumTier(premiumTier);
    },

    purchasePremiumTier(premiumTier) {
      this.$store
        .dispatch("seasonPass/purchaseTier", {
          passId: this.passId,
          tierId: premiumTier.id,
        })
        .then((res) => {
          this.showPremiumActivatedAnimation = true;
          if (res && Array.isArray(res.rewards) && res.rewards.length) {
            const payload = { ...res };
            if (!payload.pass) payload.pass = this.pass;
            this.mapApiJsonSeasonPassTierRewards(payload);
          }
          this.$store.dispatch("seasonPass/loadActivePass", {
            passId: this.passId,
          });
          this.$store.dispatch("user/updateUser");
        })
        .catch((e) => console.error("[seasonPass] purchaseTier failed", e));
    },

    openDynamicPayOffer(payOffer) {
      if (!payOffer) return;
      if (!this.$whitelabel?.featuresEnabled?.v2Offers) {
        console.warn(
          "[seasonPass] v2Offers disabled — dynamic offer modal will not render"
        );
      }
      this.$store.commit("shop/setGlobalOfferData", payOffer);
      this.$store.commit("shop/setShowGlobalOfferModal", true);
    },

    onUnlockLevel(level) {
      if (!this.passId || this.unlockInFlight) return;

      // Backend requires pay_offer route when set — currency purchase rejected.
      if (level && level.pay_offer) {
        this.openDynamicPayOffer(level.pay_offer);
        return;
      }

      const levelNumber = Number(level && level.level) || 0;
      const goldCost = Number(level && level.gold) || 0;
      const crownCost = Number(level && level.crowns) || 0;
      // Lock-panel UI charges crowns OR gold (crowns preferred), not both.
      const canAfford =
        crownCost > 0
          ? this.userCrowns >= crownCost
          : this.userGold >= goldCost;

      if (!canAfford) {
        // Season pass pay offer first — same flow as the task panel premium buy.
        if (this.premiumTier && this.premiumTier.pay_offer) {
          this.openDynamicPayOffer(this.premiumTier.pay_offer);
          return;
        }
        // No pay offer: attempt the purchase anyway. Backend rejects (500) and
        // pushes the bailout offer via `.user.offer.unlocked` (wsChannels mixin),
        // which opens the dynamic offer modal. Mirrors onBuyPremiumClick.
        // → fall through to purchaseLevel below.
      }

      this.unlockInFlight = true;
      this.$store
        .dispatch("seasonPass/purchaseLevel", {
          passId: this.passId,
          level: levelNumber,
        })
        .then(() => {
          this.lastUnlockedLevel = levelNumber;
          this.showLevelUnlockAnimation = true;
        })
        .catch((e) => console.error("[seasonPass] purchaseLevel failed", e))
        .finally(() => {
          this.unlockInFlight = false;
        });
    },

    onBuyTierOffer() {
      this.onBuyPremiumClick();
    },
  },
};
</script>

<style lang="scss">
.modal-season-pass {
  .season-pass-canvas {
    position: relative;
    width: 100%;
    height: 100%;
    background-position: top left;
    background-repeat: no-repeat;
    background-size: 100% 100%;
    overflow: hidden;
  }

  .close-button {
    position: absolute;
    width: 36px;
    height: 36px;
    right: 36px;
    top: 10px;
    z-index: 999;
  }

  .timer {
    position: absolute;
    top: 15px;
    left: 96px;
    z-index: 20;
    text-align: center;
    line-height: 1.4;
    // match the hourglass icon's fill — deliberately not config-driven
    color: #4de5ff;
    font-family: kanit-semibold;
    font-size: 17.6px;
    width: 140px;
    height: 25px;
    pointer-events: none;

    .icon-hourglass {
      width: 19px;
      height: 25px;
      background: url(~@/assets/img/icons/hourglass.svg) center/100% 100%
        no-repeat;
    }

    // countdown's fixed-width wrapper centers the text, so the gap to the
    // icon varies with text length — left-align for a constant gap
    .w-100 {
      justify-content: flex-start !important;
    }

    .countdown-digital {
      margin-left: 10px;
    }
  }

  .help-button {
    position: absolute;
    top: 10px;
    left: 36px;
    z-index: 20;
    height: 36px;
    width: 36px;
    // 88% size: solid orange disc reads bigger than the close button's thin
    // ring at equal size — shrink visual, keep full 36px hit area
    background: url(~@/assets/img/events/winter-bonus-jagd/btn-help.png)
      center/88% 88% no-repeat;
    cursor: pointer;

    &:hover {
      background: url(~@/assets/img/events/winter-bonus-jagd/btn-help-down.png)
        center/88% 88% no-repeat;
    }
  }

  .bg {
    position: absolute;
    left: 0;
    pointer-events: none;

    &.sidebar {
      bottom: 0;
      left: 0;
      width: 240px;
      height: 360px;
      z-index: 2;
      object-fit: contain;
      object-position: bottom left;
      display: block;
    }
  }

  .sidebar-tier-label {
    position: absolute;
    z-index: 3;
    left: 20px;
    width: 200px;
    text-align: center;
    font-family: kanit-semibold;
    text-transform: uppercase;
    font-size: 17px;
    color: white;
    text-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
    pointer-events: none;

    &.premium {
      left: 30px;
      bottom: 205px;
    }

    &.free {
      left: 30px;
      bottom: 100px;
    }
  }

  .sidebar-state-icon {
    position: absolute;
    z-index: 3;
    bottom: 180px;
    left: 170px;
    pointer-events: none;
    &.check-icon {
      width: 42px;
      height: 42px;
      background: url(~@/assets/img/events/winter-bonus-jagd/green-checkmark-circle.png)
        center/100% 100% no-repeat;
    }

    &.lock-icon {
      width: 44px;
      height: 59px;
      background: url(~@/assets/img/events/winter-bonus-jagd/gold-lock.png)
        center/100% 100% no-repeat;
    }
  }
}
</style>

<style lang="scss">
$modal-width: 1121px;
$modal-height: 715px;
$canvas-height: 630px;

.modal-season-pass {
  .modal-dialog {
    max-width: $modal-width;
    width: 100%;
    height: $modal-height;
    padding-top: 50px;

    @media (max-height: 820px), (max-width: 1250px) {
      transform: scale(0.9) !important;
      top: -50px;
    }
    @media (max-height: 730px), (max-width: 1100px) {
      transform: scale(0.8) !important;
      top: -130px;
      left: -30px;
    }
  }

  .modal-content {
    width: 100%;
    height: 100%;
    background: none;
    color: white;
    text-align: center;
    z-index: 0;
    border: none;
    border-radius: 0;
  }

  .modal-body {
    position: relative;
    flex: 0 0 $canvas-height !important;
    width: $modal-width;
    height: $canvas-height;
    padding: 0;
    margin-top: 0;
    overflow: hidden;
  }
}
</style>
